/**
 * Employee form — create + edit composer.
 *
 * Tailwind implementation of the sectioned employee editor in the payroll
 * prototype (`employee-edit.html`): Identity · Compensation · Bank · App
 * access. Uses `@flint/ui` primitives + Flint tokens (not the prototype CSS).
 *
 * PII (PAN / Aadhaar / bank account) is plaintext in the form; the domain
 * wraps it. App access links to an existing `memberships.id` (populated from
 * `team.list`) — "Not linked" is the default for the majority.
 *
 * @see prototype/payroll/employee-edit.html
 */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Check, Loader2, ShieldCheck } from "lucide-react";
import {
  Alert,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { EMPTY_FORM, type EmployeeFormValues } from "../lib/form-defaults.ts";
import { EmployeeAvatar } from "./employee-avatar.tsx";
import { EmployeePhotoUpload } from "./employee-photo-upload.tsx";

const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const panRegex = /^[A-Z]{5}\d{4}[A-Z]$/;

/** Form schema — refinements mirror the contract; empty optionals pass. */
const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(160),
  designation: z.string().trim().max(120),
  employeeCode: z.string().trim().max(40),
  pan: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => v === "" || panRegex.test(v), { message: "PAN like ABCDE1234F" }),
  aadhaar: z
    .string()
    .transform((v) => v.replace(/\s/g, ""))
    .refine((v) => v === "" || /^\d{12}$/.test(v), { message: "Aadhaar is 12 digits" }),
  dateOfJoining: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a joining date"),
  monthlyGross: z.string().refine((v) => /^\d+(\.\d{1,2})?$/.test(v) && Number(v) > 0, {
    message: "Enter the monthly gross",
  }),
  defaultPf: z.string().refine((v) => v === "" || /^\d+(\.\d{1,2})?$/.test(v), {
    message: "Amount in ₹",
  }),
  defaultPt: z.string().refine((v) => v === "" || /^\d+(\.\d{1,2})?$/.test(v), {
    message: "Amount in ₹",
  }),
  payMode: z.enum(["neft", "upi", "cheque"]),
  bankName: z.string().trim().max(120),
  bankAccount: z
    .string()
    .transform((v) => v.replace(/\s/g, ""))
    .refine((v) => v === "" || /^\d{9,18}$/.test(v), { message: "9–18 digit account number" }),
  bankIfsc: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => v === "" || ifscRegex.test(v), { message: "IFSC like HDFC0001789" }),
  membershipId: z.string(),
});

const PAY_MODE_OPTIONS = [
  { value: "neft", label: "Bank transfer (NEFT)" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
] as const;

const NO_LOGIN = "__none__";

export interface EmployeeFormProps {
  defaultValues?: EmployeeFormValues;
  submitLabel: string;
  pendingLabel: string;
  pending?: boolean;
  serverError?: string | null;
  onSubmit: (values: EmployeeFormValues) => Promise<void> | void;
  mode?: "create" | "edit";
  backHref: string;
  /** Edit mode only — enables the profile-photo uploader. */
  employeeId?: string;
  /** Current profile photo URL (edit mode). */
  photoUrl?: string | null;
}

export function EmployeeForm({
  defaultValues = EMPTY_FORM,
  submitLabel,
  pendingLabel,
  pending = false,
  serverError = null,
  onSubmit,
  mode = "create",
  backHref,
  employeeId,
  photoUrl = null,
}: EmployeeFormProps) {
  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onChange",
  });

  // App-access picker — populated from the existing team/members query.
  const team = trpc.team.list.useQuery({});
  const members = team.data?.members ?? [];

  const watchedName = form.watch("name");

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex h-full flex-col bg-bg">
        {/* Docked header — full content width, like the invoice/bill composer */}
        <header className="shrink-0 border-b border-line bg-paper">
          <div className="flex items-center gap-3 px-6 py-3">
            <Link
              to={backHref}
              aria-label="Back"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-line text-ink hover:bg-bg"
            >
              <ArrowLeft size={18} strokeWidth={2.2} aria-hidden />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-bold text-ink">
                {mode === "edit" ? "Edit employee" : "Add employee"}
              </h1>
              <p className="mt-0.5 text-xs text-muted sm:text-sm">
                Master record — pre-fills payroll runs. Doesn't compute statutory deductions.
              </p>
            </div>
          </div>
        </header>

        {/* Scrollable body — the gray middle scrolls between docked header + footer */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-bg">
          <div className="mx-auto w-full max-w-5xl space-y-4 px-6 py-5">
            {/* Profile photo — uploader in edit mode (employee exists), else a prompt */}
            <section className="rounded-md border border-line bg-paper p-4 shadow-sm">
              {mode === "edit" && employeeId ? (
                <EmployeePhotoUpload employeeId={employeeId} name={watchedName || "?"} initialUrl={photoUrl} />
              ) : (
                <div className="flex items-center gap-4">
                  <EmployeeAvatar name={watchedName || "?"} size="lg" />
                  <div>
                    <h3 className="text-sm font-semibold text-ink">Profile photo</h3>
                    <p className="mt-0.5 text-xs text-muted">
                      Save the employee first, then add a profile photo from their detail page.
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* Identity */}
            <FormSection title="Identity" description="Who this person is. Name appears on the payslip.">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField name="name" label="Full name" placeholder="Noushad K" required />
                <TextField name="designation" label="Designation" placeholder="Senior Engineer" />
                <TextField name="employeeCode" label="Employee code" placeholder="DNL-001" />
                <TextField name="dateOfJoining" label="Date of joining" type="date" required />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField
                  name="pan"
                  label="PAN"
                  badge="encrypted"
                  placeholder="ABCPN1234K"
                  className="font-mono uppercase"
                  helper="Optional. Stored encrypted; used on the payslip + (later) Form 16."
                />
                <TextField
                  name="aadhaar"
                  label="Aadhaar"
                  badge="encrypted"
                  placeholder="1234 5678 9012"
                  className="font-mono"
                  helper="Optional. Stored encrypted; masked on screen."
                />
              </div>
            </FormSection>

            {/* Compensation */}
            <FormSection
              title="Compensation"
              description="Pre-fills each payroll run. TDS is typed per run — never auto-calculated."
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField
                  name="monthlyGross"
                  label="Monthly gross (₹)"
                  type="number"
                  inputMode="numeric"
                  placeholder="120000"
                  required
                />
                <EnumField
                  name="payMode"
                  label="Pay mode"
                  options={PAY_MODE_OPTIONS}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField
                  name="defaultPf"
                  label="Default PF (₹)"
                  type="number"
                  inputMode="numeric"
                  placeholder="1800"
                  helper="Pre-filled deduction. Editable per run."
                />
                <TextField
                  name="defaultPt"
                  label="Default Professional Tax (₹)"
                  type="number"
                  inputMode="numeric"
                  placeholder="208"
                  helper="Editable per run."
                />
              </div>
            </FormSection>

            {/* Bank */}
            <FormSection
              title="Bank account"
              badge="encrypted"
              description="Where the net salary is paid."
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField name="bankName" label="Bank name" placeholder="HDFC Bank · Kattoor" />
                <TextField
                  name="bankIfsc"
                  label="IFSC"
                  placeholder="HDFC0001789"
                  className="font-mono uppercase"
                />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <TextField
                  name="bankAccount"
                  label="Account number"
                  placeholder="50100447827782"
                  className="font-mono"
                />
              </div>
            </FormSection>

            {/* App access */}
            <FormSection
              title="App access"
              optional
              description="Link this employee to a Flint login — only if they sign in. Most employees don't. App role is managed in Settings ▸ Team & roles."
            >
              <FormField
                control={form.control}
                name="membershipId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Linked login</FormLabel>
                    <Select
                      value={field.value || NO_LOGIN}
                      onValueChange={(v) => field.onChange(v === NO_LOGIN ? "" : v)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Not linked" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_LOGIN}>Not an app user</SelectItem>
                        {members.map((m) => (
                          <SelectItem key={m.membershipId} value={m.membershipId}>
                            {m.user.name} ({m.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Optional. Payroll and app role are managed separately.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
          </div>
        </div>

        {/* Docked footer — full content width, actions pinned bottom-right */}
        <footer className="shrink-0 border-t border-line bg-paper px-6 py-3 shadow-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1 text-sm text-muted">
              {mode === "edit" ? "Editing " : "New employee · "}
              <strong className="text-ink">{watchedName || "—"}</strong>
              {mode === "edit" ? " · changes apply to future payroll runs" : ""}
            </div>
            <Link
              to={backHref}
              className="inline-flex h-10 items-center rounded-sm px-3 text-sm font-semibold text-ink hover:bg-bg"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pending ? (
                <Loader2 size={15} className="animate-spin" aria-hidden />
              ) : (
                <Check size={15} aria-hidden />
              )}
              {pending ? pendingLabel : submitLabel}
            </button>
          </div>
          {serverError ? (
            <div className="mt-3">
              <Alert intent="bad" title="Couldn't save employee" description={serverError} />
            </div>
          ) : null}
        </footer>
      </form>
    </Form>
  );
}

// ── section + field helpers ──────────────────────────────────────

function FormSection({
  title,
  description,
  badge,
  optional,
  children,
}: {
  title: string;
  description: string;
  badge?: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="border-b border-line px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          {title}
          {badge ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
              <ShieldCheck size={11} aria-hidden />
              {badge}
            </span>
          ) : null}
          {optional ? <span className="text-xs font-normal text-muted">optional</span> : null}
        </h3>
        <p className="mt-0.5 text-sm text-muted">{description}</p>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function TextField({
  name,
  label,
  helper,
  required,
  badge,
  className,
  ...props
}: {
  name: keyof EmployeeFormValues;
  label: string;
  helper?: string;
  required?: boolean;
  badge?: string;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "name">) {
  return (
    <FormField
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            <span className="inline-flex items-center gap-2">
              {label} {required ? <span className="text-bad">*</span> : null}
              {badge ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
                  <ShieldCheck size={10} aria-hidden />
                  {badge}
                </span>
              ) : null}
            </span>
          </FormLabel>
          <FormControl>
            <Input
              {...props}
              className={className}
              value={(field.value as string) ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function EnumField<V extends string>({
  name,
  label,
  options,
}: {
  name: keyof EmployeeFormValues;
  label: string;
  options: ReadonlyArray<{ value: V; label: string }>;
}) {
  return (
    <FormField
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select value={field.value as string} onValueChange={field.onChange}>
            <FormControl>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
